import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { QuotedSourcesNodeView } from './quoted-sources-node-view'

export interface QuotedSource {
  number: number
  sourceType: string
  sourceId: string
  sourceTitle: string
  snippet?: string
  usageCount: number
}

export interface QuotedSourcesAttributes {
  sources: QuotedSource[]
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    quotedSources: {
      /**
       * Insert quoted sources block
       */
      insertQuotedSources: (attributes?: QuotedSourcesAttributes) => ReturnType
      /**
       * Update quoted sources
       */
      updateQuotedSources: (attributes: QuotedSourcesAttributes) => ReturnType
    }
  }
}

export const QuotedSourcesExtension = Node.create({
  name: 'quotedSources',

  group: 'block',

  atom: true,

  draggable: false,

  addAttributes() {
    return {
      sources: {
        default: [],
        parseHTML: (element) => {
          const sourcesAttr = element.getAttribute('data-sources')
          try {
            return sourcesAttr ? JSON.parse(sourcesAttr) : []
          } catch {
            return []
          }
        },
        renderHTML: (attributes) => {
          if (!attributes.sources || attributes.sources.length === 0) {
            return {}
          }
          return {
            'data-sources': JSON.stringify(attributes.sources),
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-quoted-sources]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-quoted-sources': '',
        class: 'quoted-sources-block',
      }),
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(QuotedSourcesNodeView)
  },

  addCommands() {
    return {
      insertQuotedSources:
        (attributes) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: attributes || { sources: [] },
          })
        },
      updateQuotedSources:
        (attributes) =>
        ({ commands, state }) => {
          const { from } = state.selection
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },
})

export default QuotedSourcesExtension
